import { ButtonStyled } from '@OnArk/styles/Buttons.styled';
import {
  CondesableSidebarStyled,
  OpenSidebarButtonStyled,
  SectionSidebarItemStyled,
} from '@OnArk/styles/CondensableSidebar.styled';

import React, { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import { MaxWidthStyled, mq } from '@hangar31/rc-layout';
import { copyToClipboard } from '@hangar31/rc-utils';
import { markStepSeen } from '@OnArk/admin/GettingStarted/markStepSeen';
import { sectionTypes } from '@OnArk/admin/Shared/Types';
import useTypePopup from '@OnArk/admin/Shared/useTypePopup';
import { useSection } from '@OnArk/contexts/section';
import NavLink from '@OnArk/NavLink';
import OrderedList, { useOrderableContext } from '@OnArk/OrderedList';
import Icon from '@OnArk/SVGs/Icon';
import posthog from '@OnArk/utils/posthog';
import { useSidebarState } from '@OnArk/utils/sidebarDefault';
import useRouteState from '@OnArk/utils/useRouteState';

import ClassSettings from './ClassSettings';
import PublishBtn from './PublishBtn';
import SectionItem from './SectionItem';

interface SectionSidebar {
  id: string;
  name: string;
  type: SectionType;
  childSections: { id: string; name: string; type: SectionType }[];
}
function SectionSidebar() {
  const { changes, church, client, course, sections } = useSection();
  const publicUrl = `${
    typeof window !== 'undefined' ? window.location.origin : 'https://onark.app'
  }/${church.url}/${course.url}`;
  const copyShareLink = copyToClipboard(publicUrl);
  const { addItem } = useOrderableContext<SectionSidebar>();
  const router = useRouter();
  const [sidebarState, setSidebarState] = useSidebarState(
    'onark-section-sidebar-state'
  );
  const [reorderOn, setReorderOn] = useRouteState(false);
  const [condensed, setCondensed] = useState(sidebarState === 'CONDENSED');

  const InsertButton = useTypePopup({
    afterInsert: (insertAt, item) => {
      void (async () => {
        const { section: newSection } = await client.createSection({
          data: { ...item, course: { connect: { id: course.id } } },
        });

        addItem(newSection, insertAt);
        void router.push(
          `/admin/classes/${course.id}/${newSection.id}/${newSection.type}`
        );
      })();
    },
    buttonProps: { itemSpacing: 1.2 },
    groups: sectionTypes,
    sm: true,
  });

  useEffect(() => {
    setCondensed(sidebarState === 'CONDENSED');
  }, [sidebarState]);

  return (
    <>
      <CondesableSidebarStyled
        dark
        condensed={condensed}
        data-tour="section-sidebar"
      >
        <MaxWidthStyled
          grid
          as="aside"
          css={{ paddingBottom: '0 !important' }}
          rowGap={0.2}
        >
          <header>
            <OpenSidebarButtonStyled
              onClick={e => {
                setSidebarState(sidebarState === 'OPEN' ? 'CONDENSED' : 'OPEN');
                setReorderOn(false);
                posthog?.capture('sideBarStateSectionNav', { sidebarState });
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Icon
                name={condensed ? 'ARROWS_Right' : 'ARROWS_Left'}
                right="0"
                size="15"
              />
            </OpenSidebarButtonStyled>

            <h1>
              <span>{course.name}</span>
            </h1>
          </header>
          <main>
            <section>
              <SectionSidebarItemStyled
                as="button"
                css={{ margin: '0 auto', width: 'calc(100% - 1.5rem)' }}
                data-tour="section-reorder"
                onClick={() => {
                  setReorderOn(!reorderOn);
                  posthog?.capture('reorderSections');
                  if (condensed) {
                    setSidebarState('OPEN');
                  }
                }}
              >
                <ButtonStyled
                  white
                  as="div"
                  css={{ opacity: 0.8, width: '100%' }}
                  withIcon={condensed ? 'only' : true}
                >
                  <Icon
                    distance={-45}
                    left="0.8"
                    name={reorderOn ? 'ACTIONS_Close' : 'ACTIONS_Move'}
                    position="right"
                    right="0.8"
                    size={condensed ? '20' : '15'}
                    tooltip={condensed ? 'Reorder' : ''}
                  />
                  <span>{reorderOn ? 'Cancel' : 'Reorder'}</span>
                </ButtonStyled>
              </SectionSidebarItemStyled>
              <MaxWidthStyled
                grid
                as="nav"
                css={theme => ({
                  '::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '::-webkit-scrollbar-thumb': {
                    background: theme.colors.white,
                    border: `4px solid ${theme.colors.clickable}`,
                    borderLeft: 'none',
                    borderRadius: '0.5rem',
                  },
                })}
                rowGap={1.2}
              >
                <SectionItem
                  condensed={condensed}
                  course={course}
                  item={sections[0]}
                  reorderOn={reorderOn}
                />
                <OrderedList<
                  SectionSidebar,
                  {
                    course: typeof course;
                    condensed: boolean;
                    reorderOn: boolean;
                  }
                >
                  hideBottomButton
                  light
                  InsertButton={InsertButton}
                  ItemRender={SectionItem}
                  freeze={!reorderOn}
                  itemProps={{ condensed, course, reorderOn }}
                  noInsertButtons={reorderOn}
                />
                <SectionSidebarItemStyled
                  css={{ margin: '0 auto', width: 'calc(100% - 2rem)' }}
                  data-tour="section-create"
                >
                  <InsertButton
                    emptyState
                    light
                    white
                    as="div"
                    css={{ i: { margin: '0 0.8rem' }, width: '100%' }}
                    emptyLabel="Create Section"
                    insertAt={sections.length - 1}
                    withIcon={condensed ? 'only' : 'sm'}
                  />
                </SectionSidebarItemStyled>

                <SectionItem
                  condensed={condensed}
                  course={course}
                  item={sections[sections.length - 1]}
                  reorderOn={reorderOn}
                />
              </MaxWidthStyled>
            </section>
          </main>
          <footer>
            <ClassSettings />
            <SectionSidebarItemStyled
              as={NavLink}
              data-tour="section-automations"
              href={`/admin/classes/${course.id}/automations`}
            >
              <Icon
                left="0.8"
                name="AUTOMATIONS_All"
                position="right"
                right="0.8"
                size="30"
                tooltip={condensed ? '' : 'Edit Automations'}
              />
              <span>Automations</span>
            </SectionSidebarItemStyled>
            <SectionSidebarItemStyled
              as="a"
              data-tour="share-preview"
              href={`/${church.url}/${course.url}?preview=true`}
              target="_blank"
              onClick={() => markStepSeen('preview')}
            >
              <Icon
                left="0.8"
                name="ACTIONS_View"
                position="right"
                right="0.8"
                size="30"
                tooltip={condensed ? 'Preview' : ''}
              />
              <span>Preview</span>
            </SectionSidebarItemStyled>
            <SectionSidebarItemStyled
              as="button"
              data-tour="share-copy-link"
              type="button"
              onClick={e => {
                const ev = e as unknown as MouseEvent;

                if (
                  changes &&
                  !confirm(
                    'Are you sure you want to share this class without publishing your updates?'
                  )
                ) {
                  return;
                }
                copyShareLink(ev);
                posthog?.capture('copyCourseLink');
                markStepSeen('share');
              }}
            >
              <Icon
                left="0.8"
                name="ACTIONS_Link"
                position="right"
                right="0.8"
                size="30"
                tooltip={condensed ? 'Copy link' : ''}
              />
              <span>Copy link</span>
            </SectionSidebarItemStyled>
            {/* Publish lives at the very bottom of the class-scoped
                footer so it's the visual terminus of the sidebar — the
                last and most prominent action you take on a class. */}
            {/* Below the md breakpoint the sidebar is force-condensed to a
                5.5rem rail (see CondensableSidebar.styled) regardless of the
                persisted `condensed` preference, so the publish button must
                use the tight rail padding there too — otherwise the wide
                1.2rem padding squeezes the icon button and it overflows/clips.
                At md+ we honor the real condensed flag. */}
            <div
              css={mq({
                padding: [
                  '0.8rem 0.6rem',
                  undefined,
                  condensed ? '0.8rem 0.6rem' : '1rem 1.2rem',
                ],
              })}
              data-tour="publish-button"
            >
              <PublishBtn />
            </div>
          </footer>
        </MaxWidthStyled>
      </CondesableSidebarStyled>
    </>
  );
}

export default SectionSidebar;
